import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import usePageMeta from 'hooks/use-page-meta';
import DataTable from 'pages/Search/components/DataTable';
import Layout from 'pages/Search/components/Layout';
import LetterFilter from 'pages/Search/components/LetterFilter';
import { useProductCatalogPayload } from 'pages/Search/components/Products/hooks/use-product-catalog-payload';
import RedirectLink from 'pages/Search/components/RedirectLink';
import SearchInput from 'pages/Search/components/SearchInput';
import SectionHeading from 'pages/Search/components/SectionHeading';
import { ROUTES } from 'pages/Search/constants';
import { normalizeName } from 'pages/Search/utils';
import { useProductsCatalog } from 'queries/catalog/use-products-catalog';

import useStyles from './styles';

const Products: React.FC = () => {
    const { classes } = useStyles();

    const layoutRef = useRef<{ scrollTop: VoidFunction } | null>(null);
    const [payload, actions] = useProductCatalogPayload();
    const { data, isLoading } = useProductsCatalog(payload);

    useEffect(() => {
        if (data && layoutRef.current) {
            layoutRef.current.scrollTop();
        }
    }, [data]);

    const hasMetaPagination = payload.page > 1;
    const pageMeta = hasMetaPagination ? ' - Page ' + payload.page : '';

    usePageMeta({
        title: `Vulnerabilities by products${
            payload.letter ? ' - ' + payload.letter.toUpperCase() : ''
        }${pageMeta} | Vulners.com`,
        description: payload.letter
            ? `Vulnerabilities by product names starting with ${payload.letter}${pageMeta}`
            : `Vulnerabilities by product names${pageMeta}`
    });

    const getOptionHref = useCallback((letter: string) => actions.url({ letter }), [actions]);
    const getPaginationHref = useCallback((page: number) => actions.url({ page }), [actions]);

    const tableHeaders = useMemo(
        () => [
            { field: 'vendor' as const, label: 'Vendor', sort: true },
            { field: 'product' as const, label: 'Product', sort: true },
            { field: 'cves' as const, label: 'Vulnerabilities', sort: true, width: 120 }
        ],
        []
    );

    const tableData = useMemo(
        () =>
            data?.items.map(({ vendor, product, cves }, index) => [
                <RedirectLink
                    key={`vendor-${index}`}
                    to={ROUTES.VENDOR({ page: null, vendor })}
                    label={normalizeName(vendor)}
                    active={!!vendor}
                    className={classes.link}
                />,
                <RedirectLink
                    key={`product-${index}`}
                    to={ROUTES.PRODUCT_VERSIONS({
                        page: null,
                        vendor: vendor,
                        product: product
                    })}
                    label={normalizeName(product)}
                    active={!!vendor && !!product}
                    className={classes.link}
                />,
                <RedirectLink
                    key={`cves-${index}`}
                    to={ROUTES.PRODUCT_CVES({
                        page: null,
                        vendor: vendor,
                        product: product
                    })}
                    label={cves}
                    active={!!vendor && !!product && cves > 0}
                    className={classes.link}
                />
            ]) ?? [],
        [classes.link, data?.items]
    );

    const handlePageChange = useCallback((page: number) => actions.set({ page }), [actions]);
    const handleLetterSelect = useCallback((letter: string) => actions.set({ letter }), [actions]);
    const handlePageSizeChange = useCallback((size: number) => actions.set({ size }), [actions]);

    return (
        <Layout
            ref={layoutRef}
            scrollable
            main={
                <div className={classes.content}>
                    <SectionHeading
                        title={['Products', payload.letter?.toUpperCase()]}
                        total={data?.total ?? NaN}
                        breadcrumbs={[
                            { to: ROUTES.PRODUCTS(), label: 'Products' },
                            payload.letter
                                ? {
                                      to: ROUTES.PRODUCTS({ letter: payload.letter }),
                                      label: payload.letter.toUpperCase()
                                  }
                                : null
                        ]}
                    />
                    <SearchInput
                        value={payload.search}
                        placeholder="Search by product name"
                        onChange={(value) => actions.set({ search: value ?? undefined })}
                    />
                    <LetterFilter
                        total={data?.total ?? NaN}
                        selected={payload.letter}
                        getOptionHref={getOptionHref}
                        onSelect={handleLetterSelect}
                    />
                    <DataTable
                        headers={tableHeaders}
                        data={tableData}
                        loading={isLoading}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        getPaginationHref={getPaginationHref}
                        onSortingChange={(sort) => actions.set({ sort })}
                        page={payload.page}
                        pageSize={payload.size}
                        totalCount={data?.total}
                        sorting={payload.sort}
                    />
                </div>
            }
        />
    );
};

export default Products;
